// Rich text editor NOTE: locales need to be inported here in order to work
import 'froala-editor/js/languages/it';
import 'froala-editor/js/languages/fr';
import 'froala-editor/js/languages/zh_cn';
import 'froala-editor/js/languages/zh_tw';
import 'froala-editor/js/languages/de';
import 'froala-editor/js/languages/es';
import 'froala-editor/js/languages/el';
import 'froala-editor/js/languages/ko';
// cronstrue resources
import 'cronstrue/locales/fr';
import 'cronstrue/locales/es';
import 'cronstrue/locales/de';
import 'cronstrue/locales/it';
import 'cronstrue/locales/ja';
import 'cronstrue/locales/zh_TW';
import 'cronstrue/locales/zh_CN';
import 'cronstrue/locales/ko';

// Emoji-bar resources
import emoji_de from '@emoji-mart/data/i18n/de.json';
import emoji_en from '@emoji-mart/data/i18n/en.json';
import emoji_es from '@emoji-mart/data/i18n/es.json';
import emoji_fr from '@emoji-mart/data/i18n/fr.json';
import emoji_it from '@emoji-mart/data/i18n/it.json';
import emoji_ja from '@emoji-mart/data/i18n/ja.json';
import emoji_ko from '@emoji-mart/data/i18n/kr.json';
import emoji_zh from '@emoji-mart/data/i18n/zh.json';
// Date picker Flatpickr
import datepicker_de from 'flatpickr/dist/l10n/de';
import { english as datepicker_en } from 'flatpickr/dist/l10n/default';
import datepicker_es from 'flatpickr/dist/l10n/es';
import datepicker_fr from 'flatpickr/dist/l10n/fr';
import datepicker_gr from 'flatpickr/dist/l10n/gr';
import datepicker_it from 'flatpickr/dist/l10n/it';
import datepicker_ja from 'flatpickr/dist/l10n/ja';
import datepicker_ko from 'flatpickr/dist/l10n/ko';
import datepicker_zh_CN from 'flatpickr/dist/l10n/zh';
import datepicker_zh_TW from 'flatpickr/dist/l10n/zh-tw';
// // Front-end application resources
import app_en from './en.json';

const generateTestKeys = (messages) => {
  return Object.keys(messages).reduce((acc, key) => {
    acc[key] = key;
    return acc;
  }, {});
};

// This function is used by the backend to load the locale files
// This will generate a separate bundle for each locale, avoiding to load all the locales at once
// It's not used with regular expression to allow webpack to do code splitting
export const loadLocale = (locale: string): Promise<any> => {
  return new Promise((res) => {
    switch (locale) {
      case 'de':
        import('./de.json').then((data) => {
          res(data?.default);
        });
        break;
      case 'es':
        import('./es.json').then((data) => {
          res(data?.default);
        });
        break;
      case 'el':
        import('./el.json').then((data) => {
          res(data?.default);
        });
        break;
      case 'fr':
        import('./fr.json').then((data) => {
          res(data?.default);
        });
        break;
      case 'fr-CA':
        import('./fr_CA.json').then((data) => {
          res(data?.default);
        });
        break;
      case 'it':
        import('./it.json').then((data) => {
          res(data?.default);
        });
        break;
      case 'ja':
        import('./ja.json').then((data) => {
          res(data?.default);
        });
        break;
      case 'ko':
        import('./ko.json').then((data) => {
          res(data?.default);
        });
        break;
      case 'zh-TW':
        import('./zh_TW.json').then((data) => {
          res(data?.default);
        });
        break;
      case 'zh-CN':
        import('./zh_CN.json').then((data) => {
          res(data?.default);
        });
        break;
      case 'en-GB':
        res(app_en);
        break;
      case 'en-XA':
        res(generateTestKeys(app_en));
        break;
      default:
        res(app_en);
        break;
    }
  });
};

export const ALL_LOCALES: AppLocale[] = [
  'en-XA',
  'en-GB',
  'fr-CA',
  'zh-CN',
  'zh-TW',
  'it',
  'ja',
  'ko',
  'fr',
  'en',
  'el',
  'de',
  'es',
  // Add new languages here
];

// Locales supported by the application
type AppLocale =
  | 'en'
  | 'en-GB'
  | 'en-XA'
  | 'de'
  | 'es'
  | 'el'
  | 'fr'
  | 'fr-CA'
  | 'it'
  | 'ja'
  | 'ko'
  | 'zh-TW'
  | 'zh-CN';
// list of locales to display in the UI (defaulting to the first one),
// along with any other metadata we need about them
// NOTE: if you change this, ensure to update the backend
export interface LocaleConfig {
  id: AppLocale;
  name: string;
  intercomLocale: string;
  zendeskLocale: string;
  richTextEditor: string; // Note: Add the imports on the top of the file the respective locale imports in order to work properly
  cronstrueLocale: string; // Note: Add the imports on the top of the file the respective locale imports in order to work properly
  emojiBar: object;
  datePicker: unknown | null;
  dateFormat: string;
  datePlaceholder: string;
}

export interface LocaleConfigForSelect {
  id: AppLocale;
  name: string;
}

const EN_DEFAULT_LOCALE_CONFIG: LocaleConfig = {
  id: 'en',
  name: 'English (US)',
  intercomLocale: 'en',
  zendeskLocale: 'en-us',
  richTextEditor: 'en',
  cronstrueLocale: 'en',
  emojiBar: emoji_en,
  datePicker: null,
  dateFormat: 'm/d/Y',
  datePlaceholder: 'MM/DD/YYYY',
};

export const LOCALES_DISPLAY_LIST: LocaleConfig[] = [
  EN_DEFAULT_LOCALE_CONFIG,
  {
    id: 'de',
    name: 'Deutsch',
    intercomLocale: 'de',
    zendeskLocale: 'de',
    richTextEditor: 'de',
    cronstrueLocale: 'de',
    emojiBar: emoji_de,
    datePicker: datepicker_de.de,
    dateFormat: 'd.m.Y',
    datePlaceholder: 'DD.MM.YYYY',
  },
  {
    id: 'es',
    name: 'Español',
    intercomLocale: 'es',
    zendeskLocale: 'es',
    richTextEditor: 'es',
    cronstrueLocale: 'es',
    emojiBar: emoji_es,
    datePicker: datepicker_es.es,
    dateFormat: 'd/m/Y',
    datePlaceholder: 'DD/MM/YYYY',
  },
  {
    id: 'el',
    name: 'Ελληνικά',
    intercomLocale: 'el',
    zendeskLocale: 'el',
    richTextEditor: 'el',
    cronstrueLocale: 'en;', // TODO: check if they add greek in the future
    emojiBar: emoji_en, // NOTE: Not supported yet. We can translate it ourselves in a second moment as the amount of keys to translate is not too big
    datePicker: datepicker_gr.gr,
    dateFormat: 'd/m/Y',
    datePlaceholder: 'DD/MM/YYYY',
  },
  {
    id: 'fr',
    name: 'Français',
    intercomLocale: 'fr',
    zendeskLocale: 'fr',
    richTextEditor: 'fr',
    cronstrueLocale: 'fr',
    emojiBar: emoji_fr,
    datePicker: datepicker_fr.fr,
    dateFormat: 'd/m/Y',
    datePlaceholder: 'DD/MM/YYYY',
  },
  {
    id: 'fr-CA',
    name: 'Français (Canada)',
    intercomLocale: 'fr',
    zendeskLocale: 'fr',
    richTextEditor: 'fr',
    cronstrueLocale: 'fr',
    emojiBar: emoji_fr,
    datePicker: datepicker_fr.fr,
    dateFormat: 'Y-m-d',
    datePlaceholder: 'YYYY-MM-DD',
  },
  {
    id: 'it',
    name: 'Italiano',
    intercomLocale: 'it',
    zendeskLocale: 'it',
    richTextEditor: 'it',
    cronstrueLocale: 'it',
    emojiBar: emoji_it,
    datePicker: datepicker_it.it,
    dateFormat: 'd/m/Y',
    datePlaceholder: 'DD/MM/YYYY',
  },
  {
    id: 'ja',
    name: '日本語',
    intercomLocale: 'ja',
    zendeskLocale: 'ja',
    richTextEditor: 'ja',
    cronstrueLocale: 'ja',
    emojiBar: emoji_ja,
    datePicker: datepicker_ja.ja,
    dateFormat: 'Y/m/d',
    datePlaceholder: 'YYYY/MM/DD',
  },
  {
    id: 'ko',
    name: '한국어',
    intercomLocale: 'ko',
    zendeskLocale: 'ko',
    richTextEditor: 'ko',
    cronstrueLocale: 'ko',
    emojiBar: emoji_ko,
    datePicker: datepicker_ko.ko,
    dateFormat: 'Y.m.d',
    datePlaceholder: 'YYYY.MM.DD',
  },
  {
    id: 'zh-TW',
    name: '中文 (繁體)',
    intercomLocale: 'zh-TW',
    zendeskLocale: 'zh-tw',
    richTextEditor: 'zh_tw',
    cronstrueLocale: 'zh_TW',
    emojiBar: emoji_zh, // NOTE: Only localized to zh
    datePicker: datepicker_zh_TW.zh,
    dateFormat: 'Y/m/d',
    datePlaceholder: 'YYYY/MM/DD',
  },
  {
    id: 'zh-CN',
    name: '中文 (简体)',
    intercomLocale: 'zh-CN',
    zendeskLocale: 'zh-cn',
    richTextEditor: 'zh_cn',
    cronstrueLocale: 'zh_CN',
    emojiBar: emoji_zh, // NOTE: Only localized to zh
    datePicker: datepicker_zh_CN.zh,
    dateFormat: 'Y/m/d',
    datePlaceholder: 'YYYY/MM/DD',
  },
  {
    id: 'en-GB',
    name: 'English (UK)',
    intercomLocale: 'en',
    zendeskLocale: 'en-us',
    richTextEditor: 'en',
    cronstrueLocale: 'en',
    emojiBar: emoji_en,
    datePicker: { ...datepicker_en, firstDayOfWeek: 1 },
    dateFormat: 'd/m/Y',
    datePlaceholder: 'DD/MM/YYYY',
  },
];

export const LOCALES_DISPLAY_LIST_FOR_SELECT: LocaleConfigForSelect[] =
  LOCALES_DISPLAY_LIST.map(({ id, name }) => ({
    id,
    name,
  }));

export const configForLocale = (locale: string): LocaleConfig => {
  return (
    LOCALES_DISPLAY_LIST.find((l) => l.id === locale) ??
    EN_DEFAULT_LOCALE_CONFIG
  );
};

export const isValidLocale = (locale) => {
  return ALL_LOCALES.includes(locale);
};
